(function (global) {
  'use strict';

  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const MOTIFS = Object.freeze({
    danke: '60005040488832', weihnachten: '60005040521600', jubilaeum: '60005040554368',
    pflanzenfee: '60005040587136', geburtstag: '60005040619904', liebe: '60005040652672',
    entschuldigung: '60005040685440', hochzeit: '60005040718208', neutral: '60005040750976',
    ohnetext: '60005040783744'
  });
  const BAG_IDS = new Set(Object.values(MOTIFS));

  function validGroup(items, id) {
    if (typeof id !== 'string' || !UUID.test(id) || !Array.isArray(items) || items.length > 500) return null;
    const members = items.filter((item) => item && item.properties && item.properties._g24_gift_id === id);
    if (members.length < 2 || members.length > 20) return null;
    const motif = members[0].properties._g24_gift_motif;
    if (!Object.prototype.hasOwnProperty.call(MOTIFS, motif)) return null;
    const keys = new Set();
    const variants = new Set();
    let bags = 0;
    let total = 0;
    for (const item of members) {
      const property = item.properties;
      const variant = String(item.variant_id);
      if (property._g24_gift_motif !== motif || property._g24_gift_revision !== '1') return null;
      if (item.instructions && (item.instructions.can_remove === false || item.instructions.can_update_quantity === false)) return null;
      if (item.parent_relationship && item.parent_relationship.parent) return null;
      if (!Number.isSafeInteger(item.quantity) || item.quantity < 1 || (property._g24_gift_role === 'bag' ? item.quantity !== 1 : item.quantity > 99)) return null;
      if (!Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0) return null;
      if (typeof item.key !== 'string' || !item.key || item.key.length > 256 || keys.has(item.key)) return null;
      if (!/^\d{1,16}$/.test(variant) || variants.has(variant)) return null;
      if (property._g24_gift_role === 'bag') {
        if (variant !== MOTIFS[motif]) return null;
        bags += 1;
      } else if (property._g24_gift_role !== 'content' || BAG_IDS.has(variant)) return null;
      keys.add(item.key);
      variants.add(variant);
      total += item.final_line_price;
      if (!Number.isSafeInteger(total)) return null;
    }
    return bags === 1 ? { id, motif, members, total } : null;
  }

  function groups(items) {
    if (!Array.isArray(items) || items.length > 500) return [];
    const ids = [...new Set(items.map((item) => item && item.properties && item.properties._g24_gift_id))];
    return ids.map((id) => validGroup(items, id)).filter(Boolean);
  }

  function removalUpdates(items, id, motif) {
    const group = validGroup(items, id);
    if (!group || group.motif !== motif) throw new Error('gift_group_changed');
    return Object.fromEntries(group.members.map((item) => [item.key, 0]));
  }

  const api = Object.freeze({ validGroup, groups, removalUpdates, motifs: MOTIFS });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!global.document || global.GREEN24GiftCart) return;
  global.GREEN24GiftCart = api;

  const document = global.document;
  const state = new WeakMap();
  const originals = new WeakMap();
  let pending = false;
  let queued = false;

  function configuration(root) {
    try {
      const node = root.querySelector('[data-g24-gift-cart-config]');
      return node ? JSON.parse(node.textContent) : null;
    } catch (_) { return null; }
  }

  function snapshot(root) {
    return [...root.querySelectorAll('tr[data-g24-gift-id]')].map((row) => ({
      row,
      key: row.dataset.g24GiftKey,
      variant_id: row.dataset.g24GiftVariant,
      quantity: Number(row.dataset.g24GiftQuantity),
      final_line_price: Number(row.dataset.g24GiftPrice),
      instructions: { can_remove: row.dataset.g24GiftRemovable !== 'false', can_update_quantity: row.dataset.g24GiftUpdatable !== 'false' },
      parent_relationship: row.dataset.g24GiftNested === 'true' ? { parent: true } : null,
      properties: {
        _g24_gift_id: row.dataset.g24GiftId,
        _g24_gift_motif: row.dataset.g24GiftMotif,
        _g24_gift_role: row.dataset.g24GiftRole,
        _g24_gift_revision: row.dataset.g24GiftRevision
      }
    }));
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function protect(row) {
    row.classList.add('g24-gift-member');
    row.querySelectorAll('.quantity__input, .quantity__button, cart-remove-button').forEach((control) => {
      if (!originals.has(control)) originals.set(control, {
        readOnly: control.readOnly, disabled: control.disabled, hidden: control.hidden,
        ariaReadOnly: control.getAttribute('aria-readonly')
      });
      if (control.matches('.quantity__input')) {
        control.readOnly = true;
        control.setAttribute('aria-readonly', 'true');
      } else if (control.matches('.quantity__button')) control.disabled = true;
      else control.hidden = true;
    });
  }

  function unprotect(root) {
    root.querySelectorAll('.g24-gift-member').forEach((row) => {
      row.classList.remove('g24-gift-member');
      row.querySelectorAll('.quantity__input, .quantity__button, cart-remove-button').forEach((control) => {
        const original = originals.get(control);
        if (!original) return;
        if (original.readOnly !== undefined) control.readOnly = original.readOnly;
        if (original.disabled !== undefined) control.disabled = original.disabled;
        control.hidden = original.hidden;
        if (original.ariaReadOnly === null) control.removeAttribute('aria-readonly');
        else control.setAttribute('aria-readonly', original.ariaReadOnly);
        originals.delete(control);
      });
    });
    root.querySelectorAll('[data-g24-gift-generated]').forEach((node) => node.remove());
  }

  function enhance(root) {
    const config = configuration(root);
    if (!config || !config.labels || !config.motifs) return;
    const items = snapshot(root);
    const previous = state.get(root);
    const signature = JSON.stringify(items.map(({ row, ...item }) => item));
    if (previous && previous.signature === signature && previous.rows.length === items.length &&
        previous.rows.every((row, index) => row === items[index].row)) return;
    unprotect(root);
    state.set(root, { signature, rows: items.map((item) => item.row) });
    const formatter = window.g24MoneyFormatter(document.documentElement.lang || 'de', { style: 'currency', currency: config.currency });
    groups(items).forEach((group, index) => {
      const label = config.labels.gift.replace('[number]', String(index + 1)) + ' · ' + config.motifs[group.motif];
      group.members.forEach((item) => {
        const badge = element('p', 'g24-gift-member__label', label);
        badge.dataset.g24GiftGenerated = '';
        item.row.querySelector('.cart-item__details').prepend(badge);
        protect(item.row);
      });
      const firstRow = group.members[0].row;
      const box = element('div', 'g24-gift-summary');
      box.dataset.g24GiftGenerated = '';
      const price = element('p', 'g24-gift-summary__price');
      price.append(element('span', '', config.labels.total), element('strong', '', formatter.format(group.total / 100)));
      const button = element('button', 'g24-gift-summary__remove', config.labels.remove);
      button.type = 'button';
      button.dataset.g24GiftRemove = group.id;
      button.dataset.g24GiftMotif = group.motif;
      button.setAttribute('aria-label', config.labels.remove + ': ' + label);
      box.append(price, button);
      firstRow.querySelector('.cart-item__details').append(box);
    });
  }

  function enhanceAll() {
    queued = false;
    document.querySelectorAll('cart-items, cart-drawer-items').forEach(enhance);
  }

  function schedule() {
    if (queued) return;
    queued = true;
    global.requestAnimationFrame(enhanceAll);
  }

  function sectionTargets() {
    const targets = [];
    document.querySelectorAll('cart-items, cart-drawer-items').forEach((controller) => {
      if (typeof controller.getSectionsToRender !== 'function') return;
      for (const target of controller.getSectionsToRender()) {
        if (!document.getElementById(target.id)) continue;
        if (!targets.some((existing) => existing.id === target.id && existing.selector === target.selector)) targets.push(target);
      }
    });
    const ids = [...new Set(targets.map((target) => target.section))];
    if (!ids.length || ids.length > 5) throw new Error('gift_sections_unavailable');
    return { targets, ids };
  }

  function localRoute(route, suffix) {
    const url = new URL(route, global.location.origin);
    if (url.origin !== global.location.origin) throw new Error('gift_route_invalid');
    if (suffix && !url.pathname.endsWith(suffix)) url.pathname += suffix;
    return url.pathname + url.search;
  }

  async function jsonRequest(url, options) {
    const response = await global.fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options });
    const result = await response.json();
    if (!response.ok || result.errors || result.status >= 400) throw new Error('gift_cart_request_failed');
    return result;
  }

  function replacementPlan(sections, targets) {
    const parser = new DOMParser();
    return targets.map((target) => {
      const holder = document.getElementById(target.id);
      const html = sections && sections[target.section];
      if (!holder || typeof html !== 'string') throw new Error('gift_sections_incomplete');
      const source = parser.parseFromString(html, 'text/html').querySelector(target.selector);
      if (!source) throw new Error('gift_section_target_missing');
      return { target: holder.querySelector(target.selector) || holder, source };
    });
  }

  async function refresh(cart, sections, request) {
    let plan;
    try { plan = replacementPlan(sections, request.targets); }
    catch (_) {
      const url = new URL(localRoute(global.routes.cart_url), global.location.origin);
      url.searchParams.set('sections', request.ids.join(','));
      const freshSections = await jsonRequest(url.pathname + url.search);
      plan = replacementPlan(freshSections, request.targets);
    }
    for (const entry of plan) entry.target.innerHTML = entry.source.innerHTML;
    document.querySelectorAll('cart-items, cart-drawer-items, cart-drawer, #main-cart-footer').forEach((node) => {
      node.classList.toggle('is-empty', cart.item_count === 0);
    });
    const drawer = document.querySelector('cart-drawer');
    const overlay = drawer && drawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', () => drawer.close());
    enhanceAll();
    const focus = drawer && drawer.classList.contains('active')
      ? drawer.querySelector('[data-g24-gift-remove], .cart-item__name, .drawer__close')
      : document.querySelector('cart-items [data-g24-gift-remove], cart-items .cart-item__name, cart-items .title');
    if (focus) {
      if (!focus.matches('a, button, input')) focus.setAttribute('tabindex', '-1');
      focus.focus();
      if (drawer && drawer.classList.contains('active') && typeof global.trapFocus === 'function') {
        global.trapFocus(drawer.querySelector('.drawer__inner'), focus);
      }
    }
  }

  function announce(message) {
    let status = document.getElementById('G24GiftCartStatus');
    if (!status) {
      status = element('p', 'visually-hidden');
      status.id = 'G24GiftCartStatus';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      document.body.append(status);
    }
    status.textContent = message;
  }

  function showError(config, root) {
    const error = document.getElementById(root.tagName === 'CART-DRAWER-ITEMS' ? 'CartDrawer-CartErrors' : 'cart-errors');
    if (error) error.textContent = config.labels.error;
    announce(config.labels.error);
  }

  async function remove(button) {
    if (pending) return;
    const root = button.closest('cart-items, cart-drawer-items');
    const config = root && configuration(root);
    if (!config) return;
    pending = true;
    let attempted = false;
    const id = button.dataset.g24GiftRemove;
    const motif = button.dataset.g24GiftMotif;
    const oldLabel = button.textContent;
    const busyRoots = [...document.querySelectorAll('cart-items, cart-drawer, #main-cart-footer')];
    busyRoots.forEach((node) => { node.setAttribute('aria-busy', 'true'); node.classList.add('g24-gift-cart-busy'); });
    button.disabled = true;
    button.textContent = config.labels.removing;
    try {
      const sections = sectionTargets();
      const fresh = await jsonRequest(localRoute(global.routes.cart_url, '.js'));
      const updates = removalUpdates(fresh.items, id, motif);
      attempted = true;
      const result = await jsonRequest(localRoute(global.routes.cart_update_url, '.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: sections.ids, sections_url: global.location.pathname })
      });
      await refresh(result, result.sections, sections);
      announce(config.labels.removed);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'green24-gift-group', cartData: result });
      }
    } catch (_) {
      if (attempted) {
        try {
          const fresh = await jsonRequest(localRoute(global.routes.cart_url, '.js'));
          await refresh(fresh, null, sectionTargets());
          if (!fresh.items.some((item) => item.properties && item.properties._g24_gift_id === id)) announce(config.labels.removed);
          else showError(config, root);
        } catch (_) { global.location.reload(); }
      } else showError(config, root);
    } finally {
      pending = false;
      busyRoots.forEach((node) => { node.removeAttribute('aria-busy'); node.classList.remove('g24-gift-cart-busy'); });
      if (button.isConnected) { button.disabled = false; button.textContent = oldLabel; }
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    if (!target) return;
    const button = target.closest('[data-g24-gift-remove]');
    if (button) { event.preventDefault(); event.stopImmediatePropagation(); remove(button); return; }
    if (pending && target.closest('cart-items, cart-drawer, #main-cart-footer')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  ['change', 'keydown'].forEach((type) => document.addEventListener(type, (event) => {
    const input = event.target;
    if (!(input instanceof Element) || !input.matches('.quantity__input')) return;
    if (type === 'keydown' && event.key !== 'Enter') return;
    if (input.closest('.g24-gift-member') || pending && input.closest('cart-items, cart-drawer')) {
      event.preventDefault(); event.stopImmediatePropagation();
      input.value = input.getAttribute('value');
    }
  }, true));
  document.addEventListener('submit', (event) => {
    if (pending && event.target.matches('#cart, #CartDrawer-Form')) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }, true);
  new MutationObserver((changes) => {
    if (changes.some((change) => change.target instanceof Element && change.target.closest('cart-items, cart-drawer') ||
      [...change.addedNodes].some((node) => node instanceof Element &&
        (node.matches('cart-items, cart-drawer') || node.querySelector('cart-items, cart-drawer'))))) schedule();
  }).observe(document.body, { childList: true, subtree: true });
  enhanceAll();
})(typeof window === 'undefined' ? globalThis : window);
